import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { InAppMessage } from "../models/inAppMessage";
import { BehaviorSubject, firstValueFrom, map, Observable } from "rxjs";
import {
  addDoc,
  collection,
  collectionData,
  collectionSnapshots,
  deleteDoc,
  doc,
  docSnapshots,
  Firestore,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  Timestamp,
  Unsubscribe,
  getCountFromServer,
  startAfter,
  getDocsFromServer,
  Query,
  where,
} from "@angular/fire/firestore";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { SiteMessage } from "../models/site-message";
import { Site } from "../models/site";
import { Functions, httpsCallable } from "@angular/fire/functions";
import { InAppMessageAiTranslation } from "../models/in-app-message-ai-translation";
import { VersionHistory } from "../models/version-history";
import { MessageTranslation } from "../models/message-translation";
import { environment } from '../../environments/environment.development';
import { ContactResponse } from "../models/dynamics-contact-response";
import { AdminPortalUser } from "../models/admin-portal-user";
import { Nps } from "../models/nps";
import { MigratedSite } from "../models/migrated-site";
import { RfidPrices } from "../models/rfid-price";

@Injectable({
  providedIn: 'root'
})
export class RepoService {

  httpClient = inject(HttpClient);
  inAppMessagesSubject: BehaviorSubject<InAppMessage[]> = new BehaviorSubject<InAppMessage[]>([]);
  bffBaseUrl = environment.bffBaseUrl;
  private readonly firestore: Firestore = inject(Firestore);
  private readonly firebaseFunctions = inject(Functions)

  adminPortalUser: WritableSignal<AdminPortalUser | null> = signal(null);
  private adminUserUnsubscribe: Unsubscribe;


  constructor() {
    this.ListenToInAppMessages();
  }

  private getFilteredNpsQuery(filter: string): Query {
    const npsCollection = collection(this.firestore, 'nps');
    const filterConditions = [];

    switch (filter) {
      case 'Comment':
        filterConditions.push(where('feedback', '!=', ''));
        break;
      case 'iOS':
        filterConditions.push(where('os', '==', 'iOS'));
        break;
      case 'Android':
        filterConditions.push(where('os', '==', 'Android'));
        break;
      case 'Account':
        filterConditions.push(where('subscription', '!=', 'Drop in'));
        break;
      case 'Drop-in':
        filterConditions.push(where('subscription', '==', 'Drop in'));
        break;
      case 'Contact':
        filterConditions.push(where('canContact', '==', true));
        break;
    }

    return query(npsCollection, ...filterConditions);
  }

  async calculateNpsScore(filter: string): Promise<number> {
    const baseQuery = this.getFilteredNpsQuery(filter);

    const promoterQuery = query(baseQuery, where('rating', '>=', 9));
    const detractorQuery = query(baseQuery, where('rating', '<=', 6));

    try {
      const [promoterSnapshot, detractorSnapshot, totalSnapshot] = await Promise.all([
        getCountFromServer(promoterQuery),
        getCountFromServer(detractorQuery),
        getCountFromServer(baseQuery)
      ]);

      const promoterCount = promoterSnapshot.data().count;
      const detractorCount = detractorSnapshot.data().count;
      const totalCount = totalSnapshot.data().count;

      if (totalCount === 0) {
        return 0;
      }

      const promoterPercentage = (promoterCount / totalCount) * 100;
      const detractorPercentage = (detractorCount / totalCount) * 100;

      return Math.round(promoterPercentage - detractorPercentage);
    } catch (error) {
      console.error(`Error calculating NPS score for filter ${filter}:`, error);
      throw error;
    }
  }

  async calculateNpsScoreForPeriod(days: number, filter: string): Promise<{ score: number, count: number }> {
    console.log(`calculateNpsScoreForPeriod called for ${days} days with filter: ${filter}`);
    const baseFilteredQuery = this.getFilteredNpsQuery(filter);
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);

    const baseQuery = query(baseFilteredQuery, where('date', '>=', Timestamp.fromDate(startDate)));

    const promoterQuery = query(baseQuery, where('rating', '>=', 9));
    const detractorQuery = query(baseQuery, where('rating', '<=', 6));

    try {
      const [promoterSnapshot, detractorSnapshot, totalSnapshot] = await Promise.all([
        getCountFromServer(promoterQuery),
        getCountFromServer(detractorQuery),
        getCountFromServer(baseQuery)
      ]);

      const promoterCount = promoterSnapshot.data().count;
      const detractorCount = detractorSnapshot.data().count;
      const totalCount = totalSnapshot.data().count;

      console.log(`Period stats for ${days} days (${filter}): Score=${promoterCount - detractorCount}, Count=${totalCount}`);

      if (totalCount === 0) {
        return { score: 0, count: 0 };
      }

      const promoterPercentage = (promoterCount / totalCount) * 100;
      const detractorPercentage = (detractorCount / totalCount) * 100;

      return {
        score: Math.round(promoterPercentage - detractorPercentage),
        count: totalCount
      };
    } catch (error) {
      console.error(`Error calculating period stats for ${days} days with filter ${filter}:`, error);
      throw error;
    }
  }

  async startAdminUserListener(userId: string): Promise<void> {
    const userDocRef = doc(this.firestore, `adminPortalUsers/${userId}`);
    return new Promise<void>((resolve) => {
      this.adminUserUnsubscribe = onSnapshot(userDocRef, (docSnap) => {
        if (docSnap.exists()) {
          const user = docSnap.data() as AdminPortalUser;
          user.access = user.access ?? [];
          console.log("[RepoService] Admin user data updated:", user.email);
          this.adminPortalUser.set(user);
        } else {
          console.warn("[RepoService] Admin user document does not exist!");
          this.adminPortalUser.set(null);
        }
        resolve();
      }, (error) => {
        console.error("[RepoService] Firestore error listening to user document:", error);
        // Do NOT set this.adminPortalUser.set(null); here unless we are sure the user has really lost access.
        // If we set it to null, the AuthGuard will immediately redirect to login.
        // It's safer to keep the last known state and let the server deny subsequent API calls if the session is truly dead.
        resolve();
      });
    });
  }

  stopAdminUserListener() {
    if (this.adminUserUnsubscribe) {
      this.adminUserUnsubscribe();
    }
  }

  getAllAdminPortalUsers(): Observable<AdminPortalUser[]> {
    const usersCollection = collection(this.firestore, 'adminPortalUsers');
    const users$ = collectionData(usersCollection, { idField: 'id' }) as Observable<AdminPortalUser[]>;

    return users$.pipe(
      map(users => users.map(user => ({
        ...user,
        access: user.access ?? []
      })))
    );
  }

  updateAdminPortalUserAccess(userId: string, access: string[]): Promise<void> {
    const userDocRef = doc(this.firestore, `adminPortalUsers/${userId}`);
    return setDoc(userDocRef, { access }, { merge: true });
  }

  async getNpsCount(filter: string): Promise<number> {
    console.log(filter)
    const q = this.getFilteredNpsQuery(filter);
    try {
      const snapshot = await getCountFromServer(q);
      console.log(`NPS count for ${filter}:`, snapshot.data().count);
      return snapshot.data().count;
    } catch (error) {
      console.error(`Error getting NPS count for filter ${filter}:`, error);
      throw error;
    }
  }

  async getAllNpsEntries(filter: string): Promise<Nps[]> {
    const baseQuery = this.getFilteredNpsQuery(filter);

    const q = query(baseQuery, orderBy('date', 'desc'));
    const documentSnapshots = await getDocsFromServer(q);
    return documentSnapshots.docs.map(doc => {
      const data = doc.data();

      if (!data["submissionCount"]) {
        data["submissionCount"] = 1;
      }

      if (!data["uid"]) {
        data["uid"] = "---";
      }

      if (!data["os"]) {
        data["os"] = "AAA";
      }

      return {
        ...data,
        date: (data['date'] as Timestamp).toDate(),
        subscription: data['subscription'].replace("SubscriptionType.", ""),
      } as Nps;
    });
  }

  async getNpsEntries(filter: string, startAfterDoc: any): Promise<{ entries: Nps[], lastVisible: any }> {
    console.log(`getNpsEntries called with filter: ${filter}, startAfterDoc: ${startAfterDoc ? 'yes' : 'no'}`);
    const baseQuery = this.getFilteredNpsQuery(filter);
    let q: Query;

    if (startAfterDoc) {
      q = query(
        baseQuery,
        orderBy('date', 'desc'),
        startAfter(startAfterDoc),
        limit(20)
      );
    } else {
      q = query(
        baseQuery,
        orderBy('date', 'desc'),
        limit(20)
      );
    }

    try {
      const documentSnapshots = await getDocsFromServer(q);

      const entries = documentSnapshots.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          date: (data['date'] as Timestamp).toDate(),
          subscription: (data['subscription'] as string || '').replace("SubscriptionType.", ""),
        } as Nps;
      });

      const lastVisible = documentSnapshots.docs[documentSnapshots.docs.length - 1];
      console.log(`Fetched ${entries.length} NPS entries for filter ${filter}. Last visible doc: ${lastVisible ? 'yes' : 'no'}`);
      return { entries, lastVisible };
    } catch (error) {
      console.error(`Error fetching NPS entries for filter ${filter}:`, error);
      throw error;
    }
  }

  ListenToInAppMessages() {
    console.log('ListenToInAppMessages called');
    const inAppMessageCollection = query(collection(this.firestore, 'inAppMessages'));

    collectionSnapshots(inAppMessageCollection).subscribe(res => {
      let data = res.map(value => {
        let message = value.data() as InAppMessage;
        message.id = value.id;
        message.created = (value.data()["created"])?.toDate();
        message.startDate = (value.data()["startDate"] as Timestamp | null)?.toDate();
        message.endDate = (value.data()["endDate"])?.toDate();
        return message
      })
      this.inAppMessagesSubject.next(data);
    })
  }

  createInAppMessage(message: InAppMessage) {
    console.log(message);
    let t = collection(this.firestore, "inAppMessages")
    addDoc(t, { ...message }).then()
  }

  async removeInAppMessage(message: InAppMessage) {
    let docRef = doc(this.firestore, `inAppMessages/${message.id}`);
    await deleteDoc(docRef);
  }

  async fetchPlaces(input: string) {
    let res = this.httpClient.get("https://maps.googleapis.com/maps/api/place/autocomplete/json?input=" + input + "&types=establishment&key=AIzaSyAMIbjK6DzE0-NMSxC7vCqy2lDxhvud8Dk")
    let val = await firstValueFrom(res)
    console.log(val)
  }

  async translateInAPPMessage(title: string, message: string, label: string): Promise<InAppMessageAiTranslation> {
    const translateMessageCallable = httpsCallable(this.firebaseFunctions, "translateMessageWithAi")
    const result = await translateMessageCallable({ title: title, message: message, label: label })
    return result.data as InAppMessageAiTranslation;
  }

  async translateMessage(messages: string[]): Promise<MessageTranslation> {
    const translateMessageCallable = httpsCallable(this.firebaseFunctions, "translateMessages")
    const result = await translateMessageCallable(messages)
    return result.data as MessageTranslation;
  }

  saveInHistory(message: InAppMessage) {
    let t = collection(this.firestore, "inAppMessagesHistory")
    addDoc(t, { ...message }).then()
  }

  getMessagesHistory(): Observable<InAppMessage[]> {
    const inAppMessageCollection = query(collection(this.firestore, 'inAppMessagesHistory'), orderBy('created', 'desc'), limit(20)); // Descending order

    return collectionSnapshots(inAppMessageCollection).pipe(
      map(res => res.map(value => {
        let message = value.data() as InAppMessage;
        message.id = value.id;
        message.created = (value.data()['created'] as Timestamp).toDate();
        return message;
      })));
  }

  addToFavorites(message: InAppMessage) {
    let t = collection(this.firestore, "inAppMessagesFavorites")
    addDoc(t, { ...message }).then()
  }

  getMessagesFavorites(): Observable<InAppMessage[]> {
    const inAppMessageCollection = query(collection(this.firestore, 'inAppMessagesFavorites'), orderBy('created', 'desc')); // Descending order

    return collectionSnapshots(inAppMessageCollection).pipe(
      map(res => res.map(value => {
        let message = value.data() as InAppMessage;
        message.id = value.id;
        message.created = (value.data()['created'] as Timestamp).toDate();
        return message;
      })));
  }

  async deleteMessageFromFavorites(message: InAppMessage) {
    const docRef = doc(this.firestore, "inAppMessagesFavorites", message.id);
    await deleteDoc(docRef);
  }

  listenToCurrentSiteMessage(): Observable<SiteMessage[]> {
    const siteMessageCollection = query(collection(this.firestore, 'siteMessages',), orderBy("dateAdded", "desc")); // Descending order

    return collectionSnapshots(siteMessageCollection).pipe(
      map(res => res.map(value => {
        let siteMessage = value.data() as SiteMessage;
        siteMessage.id = value.id;
        return siteMessage;
      })));
  }

  async createSiteMessage(siteMessage: SiteMessage) {
    const docRef = doc(this.firestore, `siteMessages/${siteMessage.id}`);
    await setDoc(docRef, { ...siteMessage })
    this.addToSiteMessageHistory(siteMessage);
  }

  async deleteSiteMessage(siteMessage: SiteMessage) {
    const docRef = doc(this.firestore, "siteMessages", siteMessage.id);
    await deleteDoc(docRef);
  }

  addToSiteMessageHistory(siteMessage: SiteMessage) {
    const collectionReference = collection(this.firestore, `siteMessagesHistory`);
    addDoc(collectionReference, { ...siteMessage }).then();
  }

  listenToCurrentSiteMessageHistory(): Observable<SiteMessage[]> {
    const siteMessageCollection = query(collection(this.firestore, 'siteMessagesHistory')); // Descending order

    return collectionSnapshots(siteMessageCollection).pipe(
      map(res => res.map(value => {
        let siteMessage = value.data() as SiteMessage;
        siteMessage.id = value.id;
        return siteMessage;
      })));
  }

  async updateSiteCategories(param: { data: any }) {
    const docRef = doc(this.firestore, `siteCategories/sites`);
    await setDoc(docRef, param)
  }

  listenToSiteCategories(): Observable<Object | null> {
    const siteMessageDoc = doc(this.firestore, 'siteCategories', 'sites');

    return docSnapshots(siteMessageDoc).pipe(
      map(snapshot => {
        if (snapshot.exists()) {
          return snapshot.data()["data"];
        } else {
          return null;
        }
      })
    );
  }

  async updatePrices(param: { data: any }) {
    const docRef = doc(this.firestore, `categoryPrices/prices`);
    await setDoc(docRef, param)
  }

  listenToCategoryPrices(): Observable<Map<string, Map<string, Object>> | null> {
    const categoryPricesDoc = doc(this.firestore, 'categoryPrices', 'prices');

    return docSnapshots(categoryPricesDoc).pipe(
      map(snapshot => {
        if (snapshot.exists()) {
          return snapshot.data()["data"];
        } else {
          return null;
        }
      })
    );
  }

  listenToRfidPrices(): Observable<RfidPrices | null> {
    const rfidPricesDoc = doc(this.firestore, 'rfidPrices', 'prices');

    return docSnapshots(rfidPricesDoc).pipe(
      map(snapshot => {
        if (snapshot.exists() && snapshot.data() && snapshot.data()['prices']) {
          return snapshot.data()['prices'] as RfidPrices;
        } else {
          return null;
        }
      })
    );
  }

  async updateRfidPrices(prices: RfidPrices): Promise<void> {
    const docRef = doc(this.firestore, 'rfidPrices/prices');
    await setDoc(docRef, { prices });
  }

  getSite(filter: string): Observable<Site[]> {
    const body = {
      "searchText": filter,
      "filter": {
        "includeRoamingSites": true,
        "connectorTypeFilter": [],
        "effectFilter": {
          "minEffect": 0,
          "maxEffect": 0
        }
      }
    };

    const url = `${this.bffBaseUrl}admin/sites/search`;
    console.log(url)
    return this.httpClient.post<any[]>(url, body).pipe(
      map(data => {
        console.log(data)
        const sites = data["sites"];
        if (Array.isArray(sites)) {
          return sites.map(item => new Site(item));
        } else {
          throw new TypeError('Response is not an array');
        }
      })
    );
  }

  async getSiteMessageHistory() {
    const siteMessageCollection = query(collection(this.firestore, 'siteMessagesHistory'), limit(15));
    let snapshot = await getDocs(siteMessageCollection)
    return snapshot.docs.map(value => {
      let siteMessage = value.data() as SiteMessage;
      siteMessage.documentId = value.id;
      if (!siteMessage.dateAdded) {
        siteMessage.dateAdded = Timestamp.now();
      }
      return siteMessage;
    });
  }

  listenToSiteMessageFavorites() {
    const siteMessageFavoritesCollection = query(collection(this.firestore, 'siteMessagesFavorites'), limit(15));
    return collectionSnapshots(siteMessageFavoritesCollection).pipe(map(res => res.map(value => {
      let siteMessage = value.data() as SiteMessage;
      siteMessage.id = value.id;
      if (!siteMessage.dateAdded) {
        siteMessage.dateAdded = Timestamp.now();
      }
      return siteMessage;
    })));
  }

  addToSiteMessageFavorites(message: SiteMessage) {
    let t = collection(this.firestore, "siteMessagesFavorites")
    addDoc(t, { ...message }).then()
  }

  async deleteFavoriteSiteMessage(message: SiteMessage) {
    let docRef = doc(this.firestore, `siteMessagesFavorites/${message.id}`);
    await deleteDoc(docRef);
  }

  async saveVersionHistory(versionNumber: string, versionHistory: { en: any[]; sv: any[], date: string }) {
    const docRef = doc(this.firestore, `versionHistory/${versionNumber}`);
    await setDoc(docRef, versionHistory)
  }

  listenToVersionHistory() {
    const versionHistoryCollection = query(collection(this.firestore, 'versionHistory',), orderBy('version', 'desc'));
    return collectionSnapshots(versionHistoryCollection).pipe(map(res => {
      let v = res.map(value => {
        let versionHistory = value.data() as VersionHistory;
        versionHistory.version = value.id;
        return versionHistory;
      })
      v.sort((a, b) => {
        const aParts = a.version.split('.').map(Number);
        const bParts = b.version.split('.').map(Number);

        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          const aVal = aParts[i] || 0;
          const bVal = bParts[i] || 0;
          if (aVal > bVal) {
            return -1;
          }
          if (aVal < bVal) {
            return 1;
          }
        }
        return 0;
      })
      return v;
    }
    ));
  }

  async deleteVersionHistory(versionHistory: VersionHistory) {
    let docRef = doc(this.firestore, `versionHistory/${versionHistory.version}`);
    await deleteDoc(docRef);
  }

  getDynamicsData(email: string): Observable<ContactResponse> {
    const body = { "email": email };
    return this.httpClient.post<ContactResponse>(`${this.bffBaseUrl}admin/healthcheck`, body);
  }

  // getAzureData(email: string): Observable<{exists:boolean}> {
  //   const body = {"email": email};
  //   return this.httpClient.post<{exists:boolean}>(`${environment.merBaseUrl}process-user-management-api/v1/user-management/auth-users:query`, body);
  // }

  getFirebaseToken(mslyToken: string, accountId: string) {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${mslyToken}`
    });

    return firstValueFrom(this.httpClient.get<{ token: string }>(`${this.bffBaseUrl}bff-config/firebase-token`, {
      headers: headers,
      params: { accountId: accountId }
    }));
  }

  removeEmailFromSendGridSpam(email: string) {
    return this.httpClient.delete<ContactResponse>(`${this.bffBaseUrl}admin/spam-reports`, { params: { email } });
  }

  getMigratedSites(): Observable<MigratedSite[]> {
    return this.httpClient.get<MigratedSite[]>(`${this.bffBaseUrl}admin/sites/migrated`);
  }

  saveMigratedSite(site: MigratedSite): Observable<void> {
    return this.httpClient.post<void>(`${this.bffBaseUrl}admin/sites/migrated`, site);
  }

  deleteMigratedSite(siteId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.bffBaseUrl}admin/sites/migrated/${siteId}`);
  }
}
